import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { FilterConfig } from '../models/filterconfig.model';
import { Filter } from '../models/filter.model';
import { SingleValueFilterOption } from '../models/singlevaluefilteroption.model';
import { FilterOption } from '../interfaces/filteroption.interface';
import { RangeFilterOption } from '../models/rangefilteroption.model';
import { FilterSortService } from './filter.sort.service';
import { UrlHelper } from '../helpers/url.helper';
import { FilterCountService } from './filter.count.service';

@Injectable()
export class FilterService {
  public currentFilter$: Observable<Filter[]>;

  protected currentFilter: BehaviorSubject<Filter[]> = new BehaviorSubject<
    Filter[]
  >([]);
  private _filterConfigs: FilterConfig[];

  constructor(
    private itemFilterCountService: FilterCountService,
    private filterSortService: FilterSortService,
    private urlHelper: UrlHelper,
  ) {
    this._filterConfigs = [];
    this.currentFilter$ = this.currentFilter.asObservable();
  }

  /**
   * Set filter configs
   * @param filterConfigs: FilterConfig[]
   */
  set filterConfigs(filterConfigs: FilterConfig[]) {
    this._filterConfigs = filterConfigs;
  }

  /**
   * Listens to input from filter components, updates active filters and updates observable
   */
  public updateFilter(filters: Filter[]) {
    filters = this.itemFilterCountService.calculateCounts(filters);
    this.currentFilter.next(filters);
  }

  public initializeCurrentFilter(updateFromUrl: boolean): void {
    let filters: Filter[] = this._filterConfigs.map((filter) => {
      return this.mapFilterConfig(filter);
    });

    filters = this.itemFilterCountService.calculateCounts(filters);
    filters = filters.map((filter: Filter) => filter.clone());

    filters = filters.filter((filter: Filter) => {
      filter.options = filter.options.filter(
        (filterOption: FilterOption) => filterOption.count > 0,
      );
      return filter.options.length > 0;
    });

    this.filterSortService.sort(filters);

    if (updateFromUrl) {
      filters = this.urlHelper.updateFilterFromUrl(filters);
    }

    this.updateFilter(filters);
  }

  private mapFilterConfig(filterConfig: FilterConfig): Filter {
    const filter: Filter = new Filter();
    filter.label = filterConfig.label;
    filter.name = filterConfig.name;
    filter.options = filterConfig.options.map((option) => {
      return this.mapFilterOptionConfig(filterConfig.type, option);
    });
    return filter;
  }

  private mapFilterOptionConfig(
    filterType: string,
    filterOptionConfig: Record<string, unknown>,
  ): FilterOption {
    let filterOption: FilterOption;
    switch (filterType) {
      case 'singleValue': {
        const singleValueFilterOption = new SingleValueFilterOption();
        singleValueFilterOption.valueToMatch = <string>(
          filterOptionConfig['name']
        );
        filterOption = singleValueFilterOption;
        break;
      }
      case 'range': {
        const rangeFilterOption = new RangeFilterOption();
        rangeFilterOption.rangeStart = <number | undefined>(
          filterOptionConfig['rangeStart']
        );
        rangeFilterOption.rangeEnd = <number | undefined>(
          filterOptionConfig['rangeEnd']
        );
        filterOption = rangeFilterOption;
        break;
      }
      default:
        throw new TypeError('Unsupported filterType: ' + filterType);
    }
    filterOption.label = <string>filterOptionConfig['label'];
    filterOption.name = <string>filterOptionConfig['name'];
    return filterOption;
  }
}
